import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import apiService from '../services/apiService';
import { toast } from 'react-toastify';

const api = apiService();

// ------------------------- Async thunks -------------------------

export const fetchTotalAnalytics = createAsyncThunk(
  'admin/fetchTotalAnalytics',
  async () => {
    const res = await api.get('/admin/total-analytics');
    return res.data;
  }
);

export const addPermission = createAsyncThunk(
  'admin/addPermission',
  async (payload: { key: string }) => {
    const res = await api.post('/permission', payload);
    return res.data;
  }
);

export const fetchPermissions = createAsyncThunk(
  'admin/fetchPermissions',
  async () => {
    const res = await api.get('/permission');
    return res.data;
  }
);

export const assignUnassignPermission = createAsyncThunk(
  'admin/assignUnassignPermission',
  async ({
    userId,
    assignPermissionIds,
    unassignPermissionIds,
  }: {
    userId: string;
    assignPermissionIds?: string[];
    unassignPermissionIds?: string[];
  }) => {
    const body: Record<string, unknown> = {};
    if (assignPermissionIds) body.assignPermissionIds = assignPermissionIds;
    if (unassignPermissionIds) body.unassignPermissionIds = unassignPermissionIds;
    const res = await api.post(`/permission/${userId}`, body);
    return res.data;
  }
);

export const getUserPermissions = createAsyncThunk(
  'admin/getUserPermissions',
  async (userId: string) => {
    const res = await api.get(`/permission/${userId}`);
    return res.data;
  }
);

export const fetchUsersWithRoleAndPermission = createAsyncThunk(
  'admin/fetchUsersWithRoleAndPermission',
  async (
    params: {
      name?: string;
      role?: string;
      page?: number;
      limit?: number;
    } = {}
  ) => {
    const res = await api.get('/permission/all-users', { params });
    return res.data; // expects { data: [...], pagination: {...} }
  }
);

// ------------------------- State & Slice -------------------------

interface Permission {
  id: string;
  key: string;
  // add other fields returned by your API if any
}

interface PaginationInfo {
  currentPage: number;
  totalPages: number;
  itemsPerPage: number;
}

interface AdminState {
  permissionList: Permission[];
  totalAnalytics: any | null;
  userPermissions: any | null;
  usersWithRole: any[]; // list of users returned by /permission/all-users
  usersWithRolePagination?: PaginationInfo | null;
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  error: string | null;
}

const initialState: AdminState = {
  permissionList: [],
  totalAnalytics: null,
  userPermissions: null,
  usersWithRole: [],
  usersWithRolePagination: undefined,
  status: 'idle',
  error: null,
};

const adminSlice = createSlice({
  name: 'admin',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    // fetchTotalAnalytics
    builder
      .addCase(fetchTotalAnalytics.pending, (s) => { s.status = 'loading'; })
      .addCase(fetchTotalAnalytics.fulfilled, (s, a) => { s.totalAnalytics = a.payload; s.status = 'succeeded'; })
      .addCase(fetchTotalAnalytics.rejected, (s, a) => { s.status = 'failed'; s.error = a.error.message ?? null; });

    // addPermission
    builder
      .addCase(addPermission.fulfilled, (s, a) => {
        s.permissionList.push(a.payload);
        toast.success('Permission added');
      })
      .addCase(addPermission.rejected, (s, a) => {
        s.error = a.error.message ?? null;
        toast.error('Failed to add permission');
      });

    // fetchPermissions
    builder
      .addCase(fetchPermissions.pending, (s) => { s.status = 'loading'; })
      .addCase(fetchPermissions.fulfilled, (s, a) => { s.permissionList = a.payload; s.status = 'succeeded'; })
      .addCase(fetchPermissions.rejected, (s, a) => { s.status = 'failed'; s.error = a.error.message ?? null; });

    // assignUnassignPermission
    builder
      .addCase(assignUnassignPermission.fulfilled, (s, a) => {
        toast.success('Permissions updated for user');
        // Optionally update userPermissions if payload returns them
        s.userPermissions = a.payload.userPermissions ?? s.userPermissions;
      })
      .addCase(assignUnassignPermission.rejected, (s, a) => {
        s.error = a.error.message ?? null;
        toast.error('Failed to update permissions for user');
      });

    // getUserPermissions
    builder
      .addCase(getUserPermissions.pending, (s) => { s.status = 'loading'; })
      .addCase(getUserPermissions.fulfilled, (s, a) => { s.userPermissions = a.payload; s.status = 'succeeded'; })
      .addCase(getUserPermissions.rejected, (s, a) => { s.status = 'failed'; s.error = a.error.message ?? null; });

    // fetchUsersWithRoleAndPermission
    builder
      .addCase(fetchUsersWithRoleAndPermission.pending, (s) => { s.status = 'loading'; })
      .addCase(fetchUsersWithRoleAndPermission.fulfilled, (s, a) => {
        const { data, pagination } = a.payload;
        const requestedPage = a.meta.arg?.page ?? 1;

        s.usersWithRole = requestedPage > 1 ? [...s.usersWithRole, ...data] : data;
        s.usersWithRolePagination = pagination ?? null;
        s.status = 'succeeded';
      })
      .addCase(fetchUsersWithRoleAndPermission.rejected, (s, a) => { s.status = 'failed'; s.error = a.error.message ?? null; });
  },
});

export default adminSlice.reducer;
